import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

function screenId(screen, index) {
  return screen?.screenId || screen?.id || `screen-${index + 1}`;
}

function screenRoute(screen) {
  const metadata = screen?.metadata || {};
  const route = metadata.route || screen?.route || '';
  return `${route}${metadata.query || ''}${metadata.hash || ''}`;
}

function screenTitle(screen, index) {
  return (
    screen?.metadata?.title ||
    screen?.metadata?.pageTitle ||
    screen?.title ||
    `Captured screen ${index + 1}`
  );
}

function usePrefersReducedMotion() {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = useState(() => globalThis.matchMedia?.(query).matches || false);

  useEffect(() => {
    const media = globalThis.matchMedia?.(query);
    if (!media) return undefined;
    const onChange = (event) => setReduced(event.matches);
    media.addEventListener?.('change', onChange);
    return () => media.removeEventListener?.('change', onChange);
  }, []);

  return reduced;
}

export function FeedbackThumbStrip({
  packageId,
  screens = [],
  status = 'ready',
  onIntent,
  onActivate,
}) {
  const [expandedId, setExpandedId] = useState(null);
  const suppressClickRef = useRef(false);
  const reducedMotion = usePrefersReducedMotion();

  function activate(screen, index) {
    onActivate?.(screen, index);
  }

  function onPointerUp(event, screen, index) {
    if (event.pointerType !== 'touch') return;
    const id = screenId(screen, index);
    if (expandedId !== id) {
      suppressClickRef.current = true;
      setExpandedId(id);
      return;
    }
    suppressClickRef.current = false;
  }

  function onClick(screen, index) {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    activate(screen, index);
  }

  const sharedProps = {
    className: 'feedback-thumb-region',
    onMouseEnter: onIntent,
    onFocusCapture: onIntent,
  };

  if (status === 'loading') {
    return (
      <div
        {...sharedProps}
        className={`${sharedProps.className} is-loading`}
        data-testid={`feedback-thumb-loading-${packageId}`}
        aria-label="Loading captured screen previews"
        aria-busy="true"
      >
        {[0, 1, 2].map((item) => (
          <span className="feedback-thumb-skeleton" aria-hidden="true" key={item} />
        ))}
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div
        {...sharedProps}
        className={`${sharedProps.className} is-unavailable`}
        data-testid={`feedback-thumb-error-${packageId}`}
      >
        Previews unavailable
      </div>
    );
  }

  if (!screens.length) {
    return (
      <div
        {...sharedProps}
        className={`${sharedProps.className} is-pending`}
        data-testid={`feedback-thumb-pending-${packageId}`}
      >
        Screen previews load on demand
      </div>
    );
  }

  const expandedIndex = screens.findIndex((screen, index) => screenId(screen, index) === expandedId);
  const expandedScreen = expandedIndex >= 0 ? screens[expandedIndex] : null;

  return (
    <>
      {expandedScreen
        ? // Large floating preview of the hovered/expanded screen (tester
          // feedback TFB-01: the in-tile expansion is too small to read any
          // detail). Portaled to document.body so ancestor transforms (which
          // capture position:fixed) can never pull it off-center or clip it.
          // Inert overlay — pointer events pass through, and the thumb button
          // itself keeps the accessible name, so this is presentation only.
          createPortal(
            <div
              className="feedback-thumb-preview"
              data-testid="feedback-thumb-preview"
              aria-hidden="true"
            >
              <img src={expandedScreen.screenshotDataUrl} alt="" />
              <span className="feedback-thumb-preview-caption">
                {`Screen ${expandedIndex + 1}: ${screenTitle(expandedScreen, expandedIndex)}`}
              </span>
            </div>,
            document.body,
          )
        : null}
    <ul
      {...sharedProps}
      className={`feedback-thumb-strip${reducedMotion ? ' is-reduced-motion' : ''}`}
      aria-label="Captured screens"
    >
      {screens.map((screen, index) => {
        const id = screenId(screen, index);
        const route = screenRoute(screen);
        const title = screenTitle(screen, index);
        const label = `Screen ${index + 1}: ${title}${route ? `, ${route}` : ''}`;
        return (
          <li className="feedback-thumb-item" key={id}>
            <button
              type="button"
              className={`feedback-thumb${expandedId === id ? ' is-expanded' : ''}`}
              aria-label={label}
              onMouseEnter={() => setExpandedId(id)}
              onMouseLeave={() => setExpandedId((current) => (current === id ? null : current))}
              onFocus={() => setExpandedId(id)}
              onBlur={() => setExpandedId((current) => (current === id ? null : current))}
              onPointerUp={(event) => onPointerUp(event, screen, index)}
              onClick={() => onClick(screen, index)}
            >
              <img src={screen.screenshotDataUrl} alt="" loading="lazy" />
              <span aria-hidden="true">{index + 1}</span>
            </button>
          </li>
        );
      })}
    </ul>
    </>
  );
}
